import { useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

const SESSION_LOGGED_KEY = "ilajly_session_logged";

/**
 * Generates a simple device fingerprint from browser properties.
 */
function generateDeviceFingerprint(): string {
  const components = [
    navigator.language,
    screen.width + "x" + screen.height,
    screen.colorDepth,
    new Date().getTimezoneOffset(),
    navigator.hardwareConcurrency || "unknown",
  ];
  const str = components.join("|");
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return "fp_" + Math.abs(hash).toString(36);
}

function parseUserAgent(ua: string) {
  let browser = "Unknown";
  let os = "Unknown";
  let platform = "Unknown";

  if (ua.includes("Chrome") && !ua.includes("Edg")) browser = "Chrome";
  else if (ua.includes("Safari") && !ua.includes("Chrome")) browser = "Safari";
  else if (ua.includes("Firefox")) browser = "Firefox";
  else if (ua.includes("Edg")) browser = "Edge";

  if (ua.includes("Windows")) os = "Windows";
  else if (ua.includes("Mac OS")) os = "macOS";
  else if (ua.includes("Android")) os = "Android";
  else if (ua.includes("iPhone") || ua.includes("iPad")) os = "iOS";
  else if (ua.includes("Linux")) os = "Linux";

  if (ua.includes("Mobile")) platform = "Mobile";
  else if (ua.includes("Tablet") || ua.includes("iPad")) platform = "Tablet";
  else platform = "Desktop";

  return { browser, os, platform };
}

/**
 * Tracks user sessions — logs once per browser session (not on token refresh).
 * Uses sessionStorage to deduplicate within the same tab session.
 */
export function useSessionTracking() {
  const logSession = useCallback(async (userId: string) => {
    // Deduplicate: only log once per browser session per user
    const logged = sessionStorage.getItem(SESSION_LOGGED_KEY);
    if (logged === userId) return;

    try {
      const fingerprint = generateDeviceFingerprint();
      const userAgent = navigator.userAgent;
      const { browser, os, platform } = parseUserAgent(userAgent);

      await supabase.from("user_sessions").insert({
        user_id: userId,
        device_fingerprint: fingerprint,
        user_agent: userAgent.substring(0, 500),
        browser,
        os,
        platform,
      });

      sessionStorage.setItem(SESSION_LOGGED_KEY, userId);
    } catch (error) {
      console.error("Session tracking error:", error);
    }
  }, []);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (event === "SIGNED_IN" && session?.user?.id) {
          logSession(session.user.id);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, [logSession]);
}
